import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckboxMenu } from '@/components/molecules/CheckboxMenu';
import { addAuditFilters, applyAuditFilterChange } from '@/domain/auditLog';

import type { AuditFilterBindings } from './filterBindings';
import { actionSections } from './filterOptions';

interface ActionPickerProps {
    bindings: AuditFilterBindings;
    label: string;
    /** The id of a visible label beside the picker, in a form row. */
    labelledBy?: string;
}

/**
 * The recorded actions, grouped by area and searchable. A group checkbox ticks the exact
 * actions it holds, which is how "every sign-in action" is asked for.
 */
export default function ActionPicker({ bindings, label, labelledBy }: ActionPickerProps) {
    const { t } = useTranslation();
    const { filters, facets, onChange } = bindings;
    const selected = filters.actions ?? [];
    const options = useMemo(
        () => actionSections(facets.actions, filters.actions ?? []),
        [facets.actions, filters.actions]
    );

    // Action codes are long, so past one the toggle counts them.
    let summary = t('auditLog.filters.selectedCount', { count: selected.length });
    if (selected.length === 0) summary = t('auditLog.filters.any');
    if (selected.length === 1) summary = selected[0];

    return (
        <CheckboxMenu
            label={label}
            summary={summary}
            options={options}
            selected={selected}
            // Asking for audit log views by name also lifts the default that hides them.
            onChange={(actions) =>
                onChange(applyAuditFilterChange(filters, addAuditFilters({ actions })))
            }
            searchable
            labelledBy={labelledBy}
        />
    );
}
